import useStore from 'Hooks/useStore';
import useAsync from 'Hooks/useAsync';
import { UserEntity } from 'Models/Entities';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { stringNotEmpty } from 'Util/TypeGuards';

/**
 * Resolves the booking subject's first/last name from `wizardData.userId`. For a customer booking
 * for themselves this is their own record; for staff booking on behalf it's the selected customer
 * (NOT the logged-in staff member). Falls back to the store's own name when no userId is set yet.
 *
 * `ready` is false while the staff-on-behalf customer fetch is still in flight, so callers can wait
 * before pre-filling (otherwise they'd briefly populate with the staff member's own name).
 */
export function useBookingSubjectName(subjectUserId?: string): {
	firstName: string;
	lastName: string;
	ready: boolean;
} {
	const store = useStore();
	const isStaffOnBehalf = store.isStaff && stringNotEmpty(subjectUserId) && subjectUserId !== store.userData?.id;

	const customerRequest = useAsync<UserEntity>(
		() => (isStaffOnBehalf ? fetchUserById(subjectUserId!) : Promise.resolve(null as unknown as UserEntity)),
		[isStaffOnBehalf, subjectUserId],
	);

	const ready = !isStaffOnBehalf || customerRequest.type === 'data';
	const firstName =
		isStaffOnBehalf && customerRequest.type === 'data'
			? (customerRequest.data?.firstName ?? '')
			: (store.firstName ?? '');
	const lastName =
		isStaffOnBehalf && customerRequest.type === 'data'
			? (customerRequest.data?.lastName ?? '')
			: (store.lastName ?? '');

	return { firstName, lastName, ready };
}
